import React, { useEffect, useState } from 'react';
import { Shell } from '../components/Shell';
import { Sidebar } from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Loader from '../components/Loader';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// 3- and 6-month windows removed 2026-07-30 — see ExplorerPage for the reasoning.
// This screen is still on the v1 chrome and is queued for rebuild; the option
// list is corrected here so a report cannot offer a comparison the methodology
// does not support in the meantime.
// Module-level labels: resolved via stored language, not the hook
const isEn = () => localStorage.getItem('k360-lang') === 'en';
const RANGE_OPTIONS = [
  { value: 'all-time',   label: isEn() ? 'All time' : 'Të gjitha' },
  { value: 'last-month', label: isEn() ? 'Last month' : 'Muaji i fundit' },
  { value: 'last-year',  label: isEn() ? 'Last year' : 'Vitin e fundit' }
];

const SectionTable = ({ title, total, items, scopeTotal }) => {
  const { t } = useT();
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: 16,
      marginBottom: 16
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: '1px solid #f3f4f6'
      }}>
        <h3 style={{ margin: 0, fontSize: 14, color: '#111827', fontWeight: 600 }}>{title}</h3>
        <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
          {formatNumber(total, 3)} tCO₂e
        </span>
      </div>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: '#6b7280', textAlign: 'left' }}>
            <th style={{ padding: '6px 4px', fontWeight: 500 }}>{t('repp.colLabel')}</th>
            <th style={{ padding: '6px 4px', fontWeight: 500, textAlign: 'right' }}>{t('repp.colRecords')}</th>
            <th style={{ padding: '6px 4px', fontWeight: 500, textAlign: 'right' }}>{t('repp.colQuantity')}</th>
            <th style={{ padding: '6px 4px', fontWeight: 500, textAlign: 'right' }}>{t('repp.colEmissions')}</th>
            <th style={{ padding: '6px 4px', fontWeight: 500, textAlign: 'right' }}>{t('repp.colSubtotalPct')}</th>
            {scopeTotal > 0 && (
              <th style={{ padding: '6px 4px', fontWeight: 500, textAlign: 'right' }}>{t('repp.colScopePct')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.key} style={{ borderTop: '1px solid #f9fafb' }}>
              <td style={{ padding: '6px 4px', color: '#111827' }}>{it.label}</td>
              <td style={{ padding: '6px 4px', color: '#6b7280', textAlign: 'right' }}>{it.count}</td>
              <td style={{ padding: '6px 4px', color: '#6b7280', textAlign: 'right' }}>
                {it.quantity ? `${formatNumber(it.quantity, 2)} ${it.unit || ''}` : '—'}
              </td>
              <td style={{ padding: '6px 4px', color: '#111827', textAlign: 'right', fontWeight: 500 }}>
                {formatNumber(it.emissions_tCO2e, 3)}
              </td>
              <td style={{ padding: '6px 4px', color: '#6b7280', textAlign: 'right' }}>
                {total > 0 ? `${((it.emissions_tCO2e / total) * 100).toFixed(1)}%` : '—'}
              </td>
              {scopeTotal > 0 && (
                <td style={{ padding: '6px 4px', color: '#9ca3af', textAlign: 'right' }}>
                  {((it.emissions_tCO2e / scopeTotal) * 100).toFixed(1)}%
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ScopeSection = ({ title, scopeTotal, children }) => (
  <section style={{ marginBottom: 32 }}>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      paddingBottom: 8,
      marginBottom: 16,
      borderBottom: '2px solid #1f2937'
    }}>
      <h2 style={{ margin: 0, fontSize: 18, color: '#111827', fontWeight: 700 }}>{title}</h2>
      <span style={{ fontSize: 16, color: '#1f2937', fontWeight: 700 }}>
        {formatNumber(scopeTotal, 3)} tCO₂e
      </span>
    </div>
    {children}
  </section>
);

const DetailedReportPage = () => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('last-year');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [buildings, setBuildings] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/buildings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(d => setBuildings(Array.isArray(d) ? d : []))
      .catch(() => setBuildings([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/';
      return;
    }
    const params = new URLSearchParams();
    params.set('range', range);
    if (buildingFilter) params.set('buildingId', buildingFilter);

    fetch(`${API_BASE}/api/dashboard?${params.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(err => {
        console.error('Failed to fetch detailed report:', err);
        setLoading(false);
      });
  }, [range, buildingFilter]);

  if (loading || !data) {
    return (
      <Shell sidebar={<Sidebar />}>
        <Navbar pageTitle={t('repp.pageTitle')} />
        <Loader />
      </Shell>
    );
  }

  const { scope1, scope2, scope3, detailedBreakdown } = data;

  return (
    <Shell sidebar={<Sidebar />}>
      <Navbar pageTitle={t('repp.pageTitle')} />
      <div style={{ padding: 32, maxWidth: 1200, margin: '0 auto', background: '#f8f9fa', minHeight: '100vh' }}>
        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          marginBottom: 24,
          padding: 12,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12
        }}>
          <label style={{ fontSize: 13, color: '#6b7280' }}>{t('repp.periodLabel')}</label>
          <select
            value={range}
            onChange={e => setRange(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
          >
            {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label style={{ fontSize: 13, color: '#6b7280', marginLeft: 16 }}>{t('repp.buildingLabel')}</label>
          <select
            value={buildingFilter}
            onChange={e => setBuildingFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
          >
            <option value="">{t('repp.allBuildings')}</option>
            {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {/* Scope 1 */}
        <ScopeSection title={t('repp.scope1Title')} scopeTotal={scope1?.total || 0}>
          {detailedBreakdown?.scope1 && (
            <>
              <SectionTable
                title={t('repp.stationaryByFuel')}
                total={detailedBreakdown.scope1.stationary.total}
                items={detailedBreakdown.scope1.stationary.items}
                scopeTotal={scope1?.total || 0}
              />
              <SectionTable
                title={t('repp.mobileByVehicle')}
                total={detailedBreakdown.scope1.mobile.total}
                items={detailedBreakdown.scope1.mobile.items}
                scopeTotal={scope1?.total || 0}
              />
              <SectionTable
                title={t('repp.processByType')}
                total={detailedBreakdown.scope1.process.total}
                items={detailedBreakdown.scope1.process.items}
                scopeTotal={scope1?.total || 0}
              />
            </>
          )}
        </ScopeSection>

        {/* Scope 2 */}
        <ScopeSection title={t('repp.scope2Title')} scopeTotal={scope2?.total || 0}>
          {detailedBreakdown?.scope2 && (
            <SectionTable
              title={t('repp.byConsumptionType')}
              total={scope2?.total || 0}
              items={detailedBreakdown.scope2.items}
              scopeTotal={scope2?.total || 0}
            />
          )}
        </ScopeSection>

        {/* Scope 3 */}
        <ScopeSection title={t('repp.scope3Title')} scopeTotal={scope3?.total || 0}>
          {detailedBreakdown?.scope3?.categories?.map(cat => (
            <SectionTable
              key={cat.key}
              title={t('repp.byActivity', { label: cat.label })}
              total={cat.emissions_tCO2e}
              items={cat.items}
              scopeTotal={scope3?.total || 0}
            />
          ))}
        </ScopeSection>

        {/* Biogenic CO₂ — peer to Scope 1/2/3, never included in Scope 1 */}
        {(detailedBreakdown?.scope1?.biogenicMemo?.total || 0) > 0 && (
          <section style={{ marginBottom: 32 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              paddingBottom: 8,
              marginBottom: 16,
              borderBottom: '2px solid #16a34a'
            }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#111827', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                Biogenic CO₂
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  background: '#dcfce7',
                  color: '#166534',
                  padding: '3px 8px',
                  borderRadius: 4,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Parallel reporting
                </span>
              </h2>
              <span style={{ fontSize: 16, color: '#1f2937', fontWeight: 700 }}>
                {detailedBreakdown.scope1.biogenicMemo.total.toFixed(3)} tCO₂e
              </span>
            </div>
            <p style={{ fontSize: 12, color: '#166534', margin: '0 0 12px', fontStyle: 'italic' }}>
              {t('repp.biogenicNote')}
            </p>
            <SectionTable
              title={t('repp.byBiogenicFuel')}
              total={detailedBreakdown.scope1.biogenicMemo.total}
              items={detailedBreakdown.scope1.biogenicMemo.items}
              scopeTotal={0}
            />
          </section>
        )}
      </div>
    </Shell>
  );
};

export default DetailedReportPage;
